import React, { useEffect, useState, useCallback } from "react";
import { ShieldCheck, Search, Loader2, Download, CheckCircle2, AlertTriangle, Circle, Filter, FileText } from "lucide-react";
import api, { API, getToken } from "@/lib/api";
import { toast } from "sonner";
import AIAuditBadge from "@/components/AIAuditBadge";

const KIND_LABEL = {
  chat: "AI-rådgivning",
  permit_check: "Bygglovs-bedömning",
  project_review: "Projektgranskning",
  drawing_review: "Ritningsgranskning",
};

const KIND_COLOR = {
  chat: "bg-blue-50 text-blue-700 border-blue-200",
  permit_check: "bg-violet-50 text-violet-700 border-violet-200",
  project_review: "bg-emerald-50 text-emerald-700 border-emerald-200",
  drawing_review: "bg-amber-50 text-amber-700 border-amber-200",
};

function summary(checklist = []) {
  const checked = checklist.filter((x) => x.status === "checked").length;
  const flagged = checklist.filter((x) => x.status === "flagged").length;
  const skipped = checklist.filter((x) => x.status === "skipped").length;
  return { checked, flagged, skipped, total: checklist.length };
}

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (kind) params.kind = kind;
      const { data } = await api.get("/ai/audit", { params });
      setRows(data);
    } catch (err) {
      toast.error("Kunde inte hämta audit-logg");
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async (auditId) => {
    try {
      const token = getToken();
      const url = `${API}/ai/audit/${auditId}/pdf${token ? `?auth=${token}` : ""}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = `ai-utlatande-${auditId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      toast.error("PDF-export misslyckades");
    }
  };

  const filtered = q
    ? rows.filter((r) => (r.audit_id || "").includes(q) || (KIND_LABEL[r.kind] || "").toLowerCase().includes(q.toLowerCase()))
    : rows;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in-up">
      <div className="flex items-start gap-3 mb-7">
        <div className="w-12 h-12 bg-blue-700 text-white rounded-sm flex items-center justify-center">
          <ShieldCheck className="w-6 h-6 stroke-[1.5]" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">Förklarbarhet · GDPR · Förvaltningslagen 29 §</div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>AI-utlåtanden – Audit-logg</h1>
          <p className="mt-1 text-sm text-zinc-600 max-w-xl">
            Alla AI-bedömningar som plattformen producerat. Klicka för att se vilka regler som kontrollerats. Exportera som PDF-bilaga till beslut.
          </p>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm" data-testid="audit-table">
        <div className="px-5 py-3 flex flex-wrap items-center gap-3 border-b border-zinc-200">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Sök audit-id eller typ..." data-testid="audit-search"
              className="w-full pl-9 pr-3 py-2 border border-zinc-300 rounded-sm text-sm focus:border-blue-700 outline-none" />
          </div>
          <div className="inline-flex items-center gap-1 text-xs text-zinc-500">
            <Filter className="w-3.5 h-3.5" /> Typ
          </div>
          <select value={kind} onChange={(e) => setKind(e.target.value)}
            data-testid="audit-kind-filter"
            className="px-3 py-2 border border-zinc-300 rounded-sm text-sm bg-white">
            <option value="">Alla typer</option>
            <option value="chat">AI-rådgivning</option>
            <option value="permit_check">Bygglovs-bedömning</option>
            <option value="project_review">Projektgranskning</option>
            <option value="drawing_review">Ritningsgranskning</option>
          </select>
          <span className="ml-auto text-xs text-zinc-500 tabular-nums">{filtered.length} poster</span>
        </div>

        {loading ? (
          <div className="px-6 py-16 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="px-6 py-12 text-center text-zinc-500 text-sm">Inga AI-utlåtanden matchar din filtrering.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider">Tid</th>
                <th className="text-left px-3 py-3 text-xs font-semibold uppercase tracking-wider">Typ</th>
                <th className="text-left px-3 py-3 text-xs font-semibold uppercase tracking-wider">Modell</th>
                <th className="text-left px-3 py-3 text-xs font-semibold uppercase tracking-wider">Logg-id</th>
                <th className="text-left px-3 py-3 text-xs font-semibold uppercase tracking-wider">Kontroller</th>
                <th className="text-left px-3 py-3 text-xs font-semibold uppercase tracking-wider">Källor</th>
                <th className="text-right px-5 py-3 text-xs font-semibold uppercase tracking-wider">Åtgärd</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const s = summary(r.checklist);
                return (
                  <tr key={r.audit_id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`audit-row-${r.audit_id}`}>
                    <td className="px-5 py-3 text-zinc-700 text-xs tabular-nums">
                      {new Date(r.created_at).toLocaleString("sv-SE")}
                    </td>
                    <td className="px-3 py-3">
                      <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase rounded-sm border ${KIND_COLOR[r.kind] || ""}`}>
                        {KIND_LABEL[r.kind] || r.kind}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-zinc-600 text-xs">{r.model || "-"}</td>
                    <td className="px-3 py-3 text-zinc-500 text-[11px] tabular-nums font-mono">{r.audit_id}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2 text-xs">
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <CheckCircle2 className="w-3 h-3" /> {s.checked}
                        </span>
                        {s.flagged > 0 && (
                          <span className="inline-flex items-center gap-1 text-amber-700">
                            <AlertTriangle className="w-3 h-3" /> {s.flagged}
                          </span>
                        )}
                        {s.skipped > 0 && (
                          <span className="inline-flex items-center gap-1 text-zinc-400">
                            <Circle className="w-3 h-3" /> {s.skipped}
                          </span>
                        )}
                        <span className="text-zinc-400">/ {s.total}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-zinc-600 text-xs">
                      {(r.sources || []).length > 0 ? (
                        <span className="inline-flex items-center gap-1">
                          <FileText className="w-3 h-3" /> {r.sources.length}
                        </span>
                      ) : (
                        <span className="text-zinc-400">–</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-1.5">
                        <AIAuditBadge auditId={r.audit_id} kind={r.kind} label="Visa" />
                        <button onClick={() => downloadPdf(r.audit_id)}
                          data-testid={`audit-pdf-${r.audit_id}`}
                          className="inline-flex items-center gap-1 text-[11px] font-medium text-zinc-700 hover:bg-zinc-100 px-2 py-1 rounded-sm border border-zinc-300 transition">
                          <Download className="w-3 h-3" /> PDF
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p className="mt-4 text-xs text-zinc-500 max-w-2xl">
        Audit-loggen är permanent och oföränderlig. Vid kommunbeslut som baseras på AI-stöd bör motsvarande PDF-bilaga
        bifogas beslutshandlingen för att uppfylla kraven på motivering enligt förvaltningslagen.
      </p>
    </div>
  );
}
